import { createContext, useContext, useMemo, useState, type JSX, type ReactNode } from 'react';
import type { MeResponse } from '@dxtech/shared';
import { isOfficeContext } from '../auth/authService.js';

export type Screen = 'home' | 'board' | 'leave' | 'trip' | 'overtime' | 'weekly' | 'expense' | 'company' | 'ci' | 'resources' | 'library' | 'picker' | 'admin' | 'approve' | 'stats' | 'peopleStats' | 'kudos' | 'assets' | 'meals' | 'settings';

interface AppCtx {
  me: MeResponse;
  env: 'desktop' | 'pane';
  screen: Screen;
  setScreen: (s: Screen) => void;
  dateOffset: number;
  setDateOffset: (n: number) => void;
  roleLabel: string;
  flash: (msg: string) => void;
  toast: string | null;
  clearToast: () => void;
}

const Ctx = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApp outside provider');
  return c;
}

function roleLabelOf(me: MeResponse): string {
  if (me.roles.includes('CEO')) return '대표이사';
  if (me.roles.includes('Executive') || me.roles.includes('Admin') || me.roles.includes('Manager')) return '관리자';
  return '사원';
}

export function AppProvider({ me, children }: { me: MeResponse; children: ReactNode }): JSX.Element {
  const [screen, setScreen] = useState<Screen>('home');
  const [dateOffset, setDateOffset] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const value = useMemo<AppCtx>(() => ({
    me,
    env: isOfficeContext() ? 'pane' : 'desktop',
    screen,
    setScreen,
    dateOffset,
    setDateOffset,
    roleLabel: roleLabelOf(me),
    flash: (msg: string) => setToast(msg),
    toast,
    clearToast: () => setToast(null),
  }), [me, screen, dateOffset, toast]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** 선택 날짜(KST 기준 today + offset)의 'YYYY-MM-DD' 와 라벨 */
export function dateForOffset(offset: number): { date: string; label: string } {
  const todayStr = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const d = new Date(`${todayStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  const date = d.toISOString().slice(0, 10);
  const wd = ['일', '월', '화', '수', '목', '금', '토'][d.getUTCDay()];
  let label = `${d.getUTCMonth() + 1}. ${d.getUTCDate()} (${wd})`;
  if (offset === 0) label += ' · 오늘';
  else if (offset === -1) label += ' · 어제';
  else if (offset === 1) label += ' · 내일';
  return { date, label };
}
